import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { isRenderableAmountScale } from '../../../shared/lib/amount';
import { requireList, UNREADABLE_RESPONSE_CODE } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export interface Position {
  id: string;
  status: string;
  portfolio_id: string;
  account_id: string;
  instrument_id: string;
  instrument_type: string;
  symbol?: string | null;
  exchange?: string | null;
  symbol_amount: number | null;
  symbol_scale: number | null;
  current_price_amount?: number | null;
  current_price_scale?: number | null;
  total_amount?: number | null;
  total_scale?: number | null;
  currency?: string | null;
  reference_time?: string | null;
  created: string;
}

interface PositionResponse {
  positions: Position[];
  cursor?: string;
}

/**
 * The whole active set of a portfolio, plus whether the client stopped short of it.
 *
 * `isTruncated` says rows the portfolio holds are absent from this listing, and it is set
 * only where that was proven — never from an outstanding cursor. An ordinary portfolio
 * exhausts its cursor before this returns, so a cursor on any page but the last says
 * nothing a reader needs to be told, and a portfolio that ends exactly on the 5,000-row
 * stop had nothing withheld.
 */
export interface PositionListing {
  positions: Position[];
  isTruncated: boolean;
}

export interface FxRate {
  symbol: string;
  rate_amount: number | null;
  rate_scale: number | null;
  reference_time: string | null;
}

export interface ConsolidatedNav {
  currency: string;
  nav_amount: number | null;
  nav_scale: number;
  rates: FxRate[];
}

export interface PositionSummary {
  portfolio_id: string;
  position_count: number;
  navs: Array<{ currency: string; nav_amount: number; nav_scale: number }>;
  consolidated_nav?: ConsolidatedNav;
  positions: Array<{ id: string; weight: string | null }>;
  allocations?: Array<{ instrument_type: string; weight: string | null }>;
}

/**
 * Why there is (or is not) a consolidated total, as one answer both cards read.
 *
 * `nav_amount: null` has two unrelated backend causes, and `navs` is what separates them:
 * a bucket exists only for a currency something was priced in, so a null alongside buckets
 * means the rate did not resolve, and a null with no buckets means nothing was priced.
 * `rates` cannot answer it — that is `[]` in both cases when nothing resolved. Specified in
 * b-cell-position-v1's README under the summary contract.
 *
 * This lives here, resolved once, because the NAV card and the allocation card were each
 * asking their own version of the question and answering it differently for one response.
 * A `PortfolioValuation` day carries the same two fields under the same contract, so the
 * NAV history chart asks this same question of each day it plots.
 */
export type ConsolidationState =
  | "none"
  | "consolidated"
  | "rate-missing"
  | "nothing-priced"
  | "unrepresentable";

export function consolidationStateOf(
  summary: { navs: unknown[]; consolidated_nav?: ConsolidatedNav | null } | null | undefined,
): ConsolidationState {
  const consolidated = summary?.consolidated_nav;
  if (consolidated == null) return "none";
  // The backend's taxonomy is keyed on the null and is checked first, so a client-side
  // limit can never be reported as one of its causes. A total the service computed and
  // this runtime cannot represent is a third thing: the rate resolved, `rates` proves it,
  // and calling that an FX outage describes an outage that did not happen.
  if (consolidated.nav_amount === null || consolidated.nav_amount === undefined) {
    return (summary?.navs.length ?? 0) > 0 ? "rate-missing" : "nothing-priced";
  }
  if (!isRenderableAmountScale(consolidated.nav_amount, consolidated.nav_scale)) {
    return "unrepresentable";
  }
  return "consolidated";
}

/** Whether the consolidated NAV was read and is below zero: a net-short book. */
export function isNegativeNavOf(
  summary: { consolidated_nav?: ConsolidatedNav | null } | null | undefined,
): boolean {
  const navAmount: unknown = summary?.consolidated_nav?.nav_amount;
  return typeof navAmount === "number" && navAmount < 0;
}

const SERVER_PAGE_SIZE = 100;
export const MAX_LISTED_POSITIONS = 5000;
/**
 * The page budget is a backstop, not the row limit: the row limit is counted in distinct
 * rows below, and pages can carry fewer of those than they hold — a repeated id, or a
 * page with no rows at all. One page beyond the rows the limit allows is what proves a
 * row was withheld, and `ceil` keeps the budget whole for constants that do not divide
 * evenly.
 */
const MAX_POSITION_PAGES = Math.ceil(MAX_LISTED_POSITIONS / SERVER_PAGE_SIZE) + 1;

async function getPositionPage(
  sessionId: string,
  scopeContext: ScopeContext,
  filters: { portfolioId?: string; cursor?: string; limit?: number },
): Promise<PositionResponse> {
  const query = new URLSearchParams();
  if (filters.portfolioId) {
    query.set('portfolio_id', filters.portfolioId);
  }
  if (filters.cursor) {
    query.set('cursor', filters.cursor);
  }
  if (filters.limit) {
    query.set('limit', String(filters.limit));
  }
  const path = query.toString() ? `/position?${query}` : '/position';
  const response = await backendClient.get(path, sessionId, scopeContext);
  if (response.success && response.data) {
    const body = response.data as { cursor?: string };
    return {
      positions: requireList<Position>(body, 'positions', 'Unreadable position response'),
      cursor: body.cursor,
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch positions');
}

/**
 * A read the caller abandoned — a navigation away, or a newer read of the same portfolio.
 * Distinct from a backend failure so a caller can tell "nobody is waiting for this" from
 * "the portfolio could not be read".
 */
export class PositionReadCanceledError extends Error {}

/**
 * Every active position of a portfolio, read by following the cursor to its end.
 *
 * The table ranks these rows client-side — `% of portfolio`, `Value` and `Price` are
 * computed at read time and stored nowhere, so no server order can produce that ranking.
 * A ranking over one arbitrary page is worse than no ranking at all: it names row 1 the
 * largest holding when the largest holding may never have been fetched. So the whole set
 * is what gets sorted, and the page keeps its loading state until this resolves.
 *
 * `limit` stays at the server's own page size. The validator caps it at 100, so a larger
 * value is rejected rather than honoured. The 5,000-row stop matches `/position/summary`,
 * which stops at the same count and logs that the summary is truncated — above it
 * the weights this table sorts on are already incomplete, so the two agree on where the
 * answer stops being whole.
 *
 * Unlike `getAllPortfolios`, a page that fails — or a read the caller abandons — aborts
 * the whole thing instead of returning what arrived so far. A partial set here is exactly
 * the confidently-wrong ranking above, and the caller has no way to tell one from a
 * complete portfolio once it is sorted. The page reports the failure and offers a retry
 * instead.
 *
 * Only one thing ends the walk claiming a whole portfolio: a cleared cursor, which is
 * what this backend sends on `NO_MORE_RESULTS`. A page with no rows is not that — a
 * Datastore query can answer with no entities and still report `NOT_FINISHED`, and this
 * read runs four equality filters plus an order, the merge-join shape that produces it.
 * Treating an empty page as the end returns a partial portfolio marked complete.
 *
 * `isTruncated` is likewise proven, never inferred from the loop falling out: it is set
 * when a page carries a row this listing will not hold, so a portfolio of exactly
 * `MAX_LISTED_POSITIONS` reports nothing missing, and a listing that does report it holds
 * exactly that many rows. The page budget bounds a server whose cursor never clears; a
 * walk that ends there is a walk that never got its answer, so it says so rather than
 * calling the rows it holds the whole portfolio.
 */
export async function getAllPositions(
  sessionId: string,
  scopeContext: ScopeContext,
  portfolioId?: string,
  options?: { isCancelled?: () => boolean },
): Promise<PositionListing> {
  const positionById = new Map<string, Position>();
  let cursor: string | undefined;
  for (let page = 0; page < MAX_POSITION_PAGES; page++) {
    if (options?.isCancelled?.()) {
      throw new PositionReadCanceledError('The position read was canceled');
    }
    const response = await getPositionPage(sessionId, scopeContext, {
      portfolioId, cursor, limit: SERVER_PAGE_SIZE,
    });
    for (const position of response.positions) {
      if (positionById.has(position.id)) continue;
      if (positionById.size >= MAX_LISTED_POSITIONS) {
        return { positions: [...positionById.values()], isTruncated: true };
      }
      positionById.set(position.id, position);
    }
    cursor = response.cursor || undefined;
    if (!cursor) {
      return { positions: [...positionById.values()], isTruncated: false };
    }
  }
  return { positions: [...positionById.values()], isTruncated: true };
}

export async function getPositionSummary(
  sessionId: string,
  scopeContext: ScopeContext,
  portfolioId: string,
  currency?: string,
): Promise<PositionSummary> {
  const response = await backendClient.get(
    `/position/summary?portfolio_id=${portfolioId}`, sessionId, scopeContext,
    currency ? { 'Accept-Currency': currency } : undefined,
  );
  if (response.success && response.data) {
    const summary = (response.data as { summary?: PositionSummary }).summary;
    if (summary == null || typeof summary !== 'object') {
      throw new BackendValidationError('Unreadable position summary response', UNREADABLE_RESPONSE_CODE);
    }
    return summary;
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch the position summary');
}
